import { ArrowLeftIcon, LightningBoltIcon } from "@radix-ui/react-icons";
import {
  Auth0AccountRole,
  MessageTemplatesService,
  TemplateRecipient,
} from "client/openapi";
import { PeopleTab } from "client/openapi/models/PeopleTab";
import { PersonKind } from "client/openapi/models/PersonKind";
import { PeopleService } from "client/openapi/services/PeopleService";
import {
  AutomationsProvider,
  useAutomations,
} from "components/Automations/hooks";
import type { Automation } from "components/Automations/types";
import { plural } from "components/Automations/words";
import { Button, ButtonColor, ButtonFill, ButtonSize } from "components/Button";
import PeoplePicker, {
  TaskPerson,
  fullName,
} from "components/Dashboard/ManageTasksDialog/PeoplePicker";
import { errorText } from "components/Forms/formsApi";
import { useAutosave } from "components/Forms/useAutosave";
import {
  EmailFrame,
  PreviewNotes,
  SegmentCount,
  TextBubble,
  useTemplatePreview,
} from "components/MessageTemplates/TemplatePreviewParts";
import { useCatalog } from "components/MessageTemplates/useCatalog";
import {
  EmailFormat,
  EmailFormatChoice,
  FormatBar,
  UploadImageButton,
  imageBlock,
  VariablePicker,
  applyEdit,
  formatted,
  imageTag,
  insertText,
} from "components/MessageTemplates/WordingTools";
import {
  openEmailBrandSettings,
  useEmailBrandSaved,
} from "components/ProfileDropdown/EmailBrandSettings";
import { OrgAdminShell } from "pages/org-performance/OrgAdminPage";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Notifications from "util/notifications";

type Wording = {
  name: string;
  email_subject: string;
  email_body: string;
  /** Which email is sent: the editor's (in the brand) or email_html. */
  email_format: EmailFormat;
  email_html: string;
  sms_body: string;
  /** Promotional: emails have an unsubscribe link. Else operational. */
  promotional: boolean;
};

type Tab = "email" | "sms";

const TYPES = [
  { value: "operational", label: "Operational" },
  { value: "promotional", label: "Promotional" },
];

const ROLE_OF_KIND: Record<string, Auth0AccountRole> = {
  [PersonKind.STUDENT]: Auth0AccountRole.ME,
  [PersonKind.PARENT]: Auth0AccountRole.PARENT,
  [PersonKind.TUTOR]: Auth0AccountRole.ORG_TUTOR,
};

/**
 * Whether an automation has a step that sends this template; with
 * onItsOwn, a step that sends it without asking first.
 */
function sends(automation: Automation, templateId: number, onItsOwn = false) {
  return automation.steps.some(
    (step) =>
      step.action === "message" &&
      step.message.templateId === templateId &&
      !(onItsOwn && step.askFirst.on)
  );
}

/**
 * Live: automations that are on send this template on their own (without
 * asking first), so what changes here goes out with their next messages,
 * as the Live banner said of triggers. The link shows them on the
 * Automations tab. Nothing shows while they load or if they can't.
 */
function LiveBanner({
  templateId,
  onOpen,
}: {
  templateId: number;
  onOpen: () => void;
}) {
  const { automations, loading, error } = useAutomations();
  if (loading || error) return null;
  const n = automations.filter(
    (a) => a.on && sends(a, templateId, true)
  ).length;
  if (!n) return null;
  return (
    <p className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900">
      <span className="font-semibold">Live:</span>{" "}
      <button
        type="button"
        className="font-medium underline hover:text-amber-950"
        onClick={onOpen}
      >
        {n === 1
          ? "1 automation sends this on its own"
          : `${plural(n, "automation")} send this on their own`}
      </button>
      . What you change here goes out with {n === 1 ? "its" : "their"} next
      messages.
    </p>
  );
}

/**
 * "Used by 2 automations": the automations whose steps send this template,
 * from the Automations area. It shows them on the Automations tab; nothing
 * shows while they load or if they can't.
 */
function UsedByAutomations({
  templateId,
  onOpen,
}: {
  templateId: number;
  onOpen: () => void;
}) {
  const { automations, loading, error } = useAutomations();
  if (loading || error) return null;
  const n = automations.filter((a) => sends(a, templateId)).length;
  return (
    <button
      type="button"
      className="mt-8 flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900"
      onClick={onOpen}
    >
      <LightningBoltIcon aria-hidden />
      <span className="underline">
        {n
          ? `Used by ${plural(n, "automation")}`
          : "Not used by any automation"}
      </span>
    </button>
  );
}

/**
 * The organization's people for "Preview as" (the People panel's All tab:
 * archived people are not on it).
 */
function usePeople(orgId: number) {
  const [people, setPeople] = useState<TaskPerson[]>();
  useEffect(() => {
    PeopleService.getPeopleRecipients({
      orgId,
      requestBody: { tab: PeopleTab.ALL },
    })
      .then((everyone) =>
        setPeople([
          ...everyone.students.map((p) => ({
            role: PersonKind.STUDENT,
            id: p.id,
            label: fullName(p),
          })),
          ...everyone.parents.map((p) => ({
            role: PersonKind.PARENT,
            id: p.id,
            label: fullName(p),
          })),
          ...everyone.tutors.map((p) => ({
            role: PersonKind.TUTOR,
            id: p.id,
            label: fullName(p),
          })),
        ])
      )
      .catch(() => setPeople([]));
  }, [orgId]);
  return people;
}

function Editor({ orgId }: { orgId: number }) {
  const { templateId } = useParams();
  const id = Number(templateId);
  const navigate = useNavigate();
  const catalog = useCatalog(orgId);
  const people = usePeople(orgId);

  const [wording, setWording] = useState<Wording>();
  const [loadError, setLoadError] = useState<string>();
  const [tab, setTab] = useState<Tab>("email");
  const [previewAs, setPreviewAs] = useState<TaskPerson[]>([]);
  const [testing, setTesting] = useState(false);
  const [lastField, setLastField] = useState<"subject" | "body" | "sms">(
    "body"
  );
  const subjectRef = useRef<HTMLInputElement>(null);
  const bodyRef = useRef<HTMLTextAreaElement>(null);
  const htmlRef = useRef<HTMLTextAreaElement>(null);
  const smsRef = useRef<HTMLTextAreaElement>(null);

  const {
    pending,
    error: saveError,
    save,
    markSaved,
  } = useAutosave(
    wording,
    (w) =>
      MessageTemplatesService.updateMessageTemplate({
        templateId: id,
        requestBody: {
          name: w.name.trim() || undefined,
          email_subject: w.email_subject,
          email_body: w.email_body,
          email_format: w.email_format,
          email_html: w.email_html,
          sms_body: w.sms_body,
          promotional: w.promotional,
        },
      }),
    800
  );

  const load = useCallback(() => {
    setLoadError(undefined);
    MessageTemplatesService.getMessageTemplate({ templateId: id })
      .then((template) => {
        const loaded = {
          name: template.name,
          email_subject: template.email_subject ?? "",
          email_body: template.email_body ?? "",
          email_format:
            template.email_format === "html"
              ? ("html" as const)
              : ("editor" as const),
          email_html: template.email_html ?? "",
          sms_body: template.sms_body ?? "",
          promotional: !!template.promotional,
        };
        markSaved(loaded);
        setWording(loaded);
      })
      .catch((e) =>
        setLoadError(errorText(e, "This template could not be loaded."))
      );
  }, [id, markSaved]);

  useEffect(load, [load]);

  const person = useMemo<TemplateRecipient | null>(
    () =>
      previewAs[0]
        ? {
            person_type: ROLE_OF_KIND[previewAs[0].role],
            person_id: Number(previewAs[0].id),
          }
        : null,
    [previewAs]
  );
  const previewRequest = useMemo(
    () =>
      wording
        ? {
            template_id: id,
            email_subject: wording.email_subject,
            email_body: wording.email_body,
            email_format: wording.email_format,
            email_html: wording.email_html,
            sms_body: wording.sms_body,
            promotional: wording.promotional,
            person,
          }
        : null,
    [id, wording, person]
  );
  // A brand change (in Organization Settings) shows in the preview: it
  // asks again.
  const [brandSaves, setBrandSaves] = useState(0);
  useEmailBrandSaved(() => setBrandSaves((n) => n + 1));
  const { preview, error: previewError } = useTemplatePreview(
    orgId,
    previewRequest,
    400,
    brandSaves
  );

  /** Done and back: save what is pending, then leave (stay on an error). */
  async function leave(to: string) {
    if (await save()) navigate(to);
  }

  const set = (changes: Partial<Wording>) =>
    setWording((current) => current && { ...current, ...changes });

  /** Text typed in at the cursor of your own HTML. */
  function typeInHtml(text: string) {
    if (!wording) return;
    applyEdit(
      htmlRef,
      insertText(htmlRef.current, wording.email_html, text),
      (v) => set({ email_html: v })
    );
  }

  /** A variable typed in at the cursor of the box last used. */
  function pickVariable(key: string) {
    if (!wording) return;
    const text = `{{${key}}}`;
    if (tab === "sms") {
      applyEdit(
        smsRef,
        insertText(smsRef.current, wording.sms_body, text),
        (v) => set({ sms_body: v })
      );
    } else if (lastField === "subject") {
      applyEdit(
        subjectRef,
        insertText(subjectRef.current, wording.email_subject, text),
        (v) => set({ email_subject: v })
      );
    } else if (wording.email_format === "html") {
      typeInHtml(text);
    } else {
      applyEdit(
        bodyRef,
        insertText(bodyRef.current, wording.email_body, text),
        (v) => set({ email_body: v })
      );
    }
  }

  function sendTest() {
    if (!wording) return;
    setTesting(true);
    MessageTemplatesService.testMessageTemplate({
      templateId: id,
      requestBody: {
        email_subject: wording.email_subject,
        email_body: wording.email_body,
        email_format: wording.email_format,
        email_html: wording.email_html,
        sms_body: wording.sms_body,
        person,
        sms: tab === "sms",
        email: tab === "email",
      },
    })
      .then((sent) =>
        Notifications.success(
          sent.email
            ? `A test email went to ${sent.email}.`
            : `A test text went to ${sent.phone}.`
        )
      )
      .catch((e) =>
        Notifications.error(errorText(e, "The test could not be sent."))
      )
      .finally(() => setTesting(false));
  }

  if (loadError) {
    return (
      <div className="py-6 px-7 rounded-xl bg-neutral-50">
        <p className="text-sm text-red-600">{loadError}</p>
        <Button
          color={ButtonColor.SKYBLUE}
          size={ButtonSize.SMALL}
          onClick={load}
          extraClasses="mt-3"
        >
          Try again
        </Button>
      </div>
    );
  }
  if (!wording) {
    return (
      <div className="py-6 px-7 rounded-xl bg-neutral-50 animate-pulse space-y-3">
        <div className="h-8 w-1/3 rounded bg-gray-200" />
        <div className="h-40 rounded bg-gray-100" />
      </div>
    );
  }

  const ownHtml = wording.email_format === "html";
  // The automations that send this template, on the Automations tab.
  const showAutomations = () => leave(`/automations?template=${id}`);
  const versions = {
    email: !!(ownHtml ? wording.email_html : wording.email_body).trim(),
    sms: !!wording.sms_body.trim(),
  };
  const tabButton = (which: Tab, label: string) => (
    <button
      key={which}
      type="button"
      aria-pressed={tab === which}
      className={`px-3 py-1 rounded-full text-sm transition-colors duration-200 ${
        tab === which
          ? "bg-[#98acfc] text-white"
          : "text-[#98acfc] hover:bg-[#98acfc]/10"
      }`}
      onClick={() => setTab(which)}
    >
      {label}
    </button>
  );

  return (
    <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr)_460px] pb-16">
      <div>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <button
            onClick={() => leave("/message-templates")}
            className="flex items-center gap-1 text-sm font-semibold text-gray-700"
          >
            <ArrowLeftIcon /> Message Templates
          </button>
          <div className="flex items-center gap-3">
            <span
              className={`text-sm ${
                saveError ? "text-red-600" : "text-gray-500"
              }`}
              aria-live="polite"
            >
              {saveError || (pending ? "Saving…" : "Saved")}
            </span>
            <Button
              color={ButtonColor.GREEN}
              size={ButtonSize.SMALL}
              onClick={() => leave("/message-templates")}
            >
              Done
            </Button>
          </div>
        </div>

        <input
          className="input mt-4 w-full text-2xl font-bold text-gray-900"
          value={wording.name}
          maxLength={80}
          aria-label="Template name"
          placeholder="Template name"
          onChange={(e) => set({ name: e.target.value })}
        />

        <AutomationsProvider orgId={orgId} loading={null} failed={() => null}>
          <LiveBanner templateId={id} onOpen={showAutomations} />
        </AutomationsProvider>

        <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
          <div
            className="inline-flex rounded-full border border-[#98acfc] p-1"
            role="group"
            aria-label="Version"
          >
            {tabButton("email", "Email")}
            {tabButton("sms", "Text")}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {catalog && (
              <VariablePicker
                groups={catalog.variables}
                onPick={pickVariable}
              />
            )}
          </div>
        </div>

        {/* Both versions stay on the page, one hidden, so switching keeps
            each box's Cmd+Z history. */}
        <div
          hidden={tab !== "email"}
          className="mt-3 rounded-lg border border-gray-200 bg-white p-4"
        >
          <label htmlFor="template-subject" className="input-label font-bold">
            Subject
          </label>
          <input
            id="template-subject"
            ref={subjectRef}
            className="input mt-0 rounded-lg"
            value={wording.email_subject}
            maxLength={200}
            placeholder="Subject"
            onFocus={() => setLastField("subject")}
            onChange={(e) => set({ email_subject: e.target.value })}
          />
          <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
            <label
              htmlFor={ownHtml ? "template-html" : "template-body"}
              className="input-label m-0 font-bold"
            >
              Email
            </label>
            <EmailFormatChoice
              value={wording.email_format}
              onChange={(format) => set({ email_format: format })}
            />
          </div>
          {/* Both emails stay on the page, one hidden, so switching keeps
              each one and its Cmd+Z history. Only the chosen one is sent. */}
          <div hidden={ownHtml}>
            <div className="flex flex-wrap items-center gap-2">
              <FormatBar
                onFormat={(format) =>
                  applyEdit(
                    bodyRef,
                    formatted(bodyRef.current, wording.email_body, format),
                    (v) => set({ email_body: v })
                  )
                }
              />
              <UploadImageButton
                orgId={orgId}
                onUploaded={(_url, name) =>
                  applyEdit(
                    bodyRef,
                    imageBlock(bodyRef.current, wording.email_body, name),
                    (v) => set({ email_body: v })
                  )
                }
              />
            </div>
            <textarea
              id="template-body"
              ref={bodyRef}
              className="input mt-1 rounded-lg"
              rows={14}
              value={wording.email_body}
              placeholder="Write your email here. Leave it empty for a text-only template."
              onFocus={() => setLastField("body")}
              onChange={(e) => set({ email_body: e.target.value })}
            />
            <p className="mt-1 text-xs text-gray-500">
              A blank line starts a new paragraph. Upload image adds a line like
              [Image: classroom.jpg], and the picture shows there. Variables
              like {"{{student.first_name}}"} are filled in for each person; you
              add a fallback for when a variable is blank, like{" "}
              {'"Hi {{student.first_name | there}}"'}. Your logo, colors, and
              font are added for you. You can change them in{" "}
              <button
                type="button"
                className="font-medium text-[#6b7fd6] hover:underline"
                onClick={openEmailBrandSettings}
              >
                Organization Settings
              </button>
              .
            </p>
          </div>
          <div hidden={!ownHtml}>
            <UploadImageButton
              orgId={orgId}
              onUploaded={(url) => typeInHtml(imageTag(url))}
            />
            <textarea
              id="template-html"
              ref={htmlRef}
              className="input mt-1 rounded-lg font-mono text-xs"
              rows={18}
              spellCheck={false}
              value={wording.email_html}
              placeholder="Paste or write your HTML here."
              onFocus={() => setLastField("body")}
              onChange={(e) => set({ email_html: e.target.value })}
            />
            <p className="mt-1 text-xs text-gray-500">
              Your HTML is sent just as you wrote it. Your logo, colors, and
              font aren&apos;t added. Variables like{" "}
              {"{{student.first_name | there}}"} are filled in for each person.
              Images need an https link, or use Upload image. Email apps
              don&apos;t run JavaScript, so we take it out, along with forms and
              embedded pages.
              {wording.promotional &&
                " Your mailing address and an unsubscribe link are added at the end."}
            </p>
          </div>
          <div className="mt-4 border-t border-gray-100 pt-3">
            <p className="input-label m-0 font-bold">Type</p>
            <div
              className="mt-1 inline-flex rounded-full border border-[#98acfc] p-0.5"
              role="group"
              aria-label="Type"
            >
              {TYPES.map((type) => {
                const on =
                  (type.value === "promotional") === wording.promotional;
                return (
                  <button
                    key={type.value}
                    type="button"
                    aria-pressed={on}
                    className={`rounded-full px-3 py-1 text-xs transition-colors duration-200 ${
                      on
                        ? "bg-[#98acfc] text-white"
                        : "text-[#98acfc] hover:bg-[#98acfc]/10"
                    }`}
                    onClick={() =>
                      set({ promotional: type.value === "promotional" })
                    }
                  >
                    {type.label}
                  </button>
                );
              })}
            </div>
            <p className="mt-1 text-xs text-gray-500">
              {wording.promotional
                ? "For newsletters and offers. These include an unsubscribe link."
                : "For reminders and notices. These always send."}
            </p>
          </div>
        </div>
        <div
          hidden={tab !== "sms"}
          className="mt-3 rounded-lg border border-gray-200 bg-white p-4"
        >
          <label htmlFor="template-sms" className="input-label font-bold">
            Text
          </label>
          <textarea
            id="template-sms"
            ref={smsRef}
            className="input mt-0 rounded-lg"
            rows={6}
            maxLength={1600}
            value={wording.sms_body}
            placeholder="Write your text here. Leave it empty for an email-only template."
            onFocus={() => setLastField("sms")}
            onChange={(e) => set({ sms_body: e.target.value })}
          />
          {preview && <SegmentCount preview={preview} />}
        </div>

        <AutomationsProvider orgId={orgId} loading={null} failed={() => null}>
          <UsedByAutomations templateId={id} onOpen={showAutomations} />
        </AutomationsProvider>
      </div>

      <div className="self-start xl:sticky xl:top-[100px] xl:max-h-[calc(100vh-120px)] xl:overflow-y-auto">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-lg font-bold text-gray-900">Preview</h3>
          <Button
            color={ButtonColor.PURPLE}
            fill={ButtonFill.HOLLOW}
            size={ButtonSize.SMALL}
            disabled={
              testing || (tab === "email" ? !versions.email : !versions.sms)
            }
            onClick={sendTest}
          >
            {tab === "email" ? "Send me a test email" : "Send me a test text"}
          </Button>
        </div>
        <label
          htmlFor="template-preview-as"
          className="mt-2 block text-xs font-semibold text-gray-600"
        >
          Preview as
        </label>
        <PeoplePicker
          inputId="template-preview-as"
          people={people ?? []}
          roles={[PersonKind.STUDENT, PersonKind.PARENT, PersonKind.TUTOR]}
          value={previewAs}
          onChange={(chosen) => setPreviewAs(chosen.slice(-1))}
          placeholder={
            preview?.person
              ? `${preview.person.name} (search for someone else)`
              : people
              ? "Search by name"
              : "Loading people..."
          }
        />
        <div className="mt-3 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
          {previewError && (
            <p className="text-sm text-red-600">{previewError}</p>
          )}
          {!preview ? (
            <div className="h-64 animate-pulse rounded bg-gray-100" />
          ) : tab === "email" ? (
            <>
              <p className="text-sm text-gray-700">
                <span className="font-semibold">Subject:</span>{" "}
                {preview.subject}
              </p>
              <div className="mt-2">
                {versions.email ? (
                  <EmailFrame html={preview.html} title="Email preview" />
                ) : (
                  <p className="text-sm text-gray-500">
                    This template has no email yet.
                  </p>
                )}
              </div>
            </>
          ) : (
            <TextBubble text={preview.sms} />
          )}
          {preview && <PreviewNotes preview={preview} />}
        </div>
      </div>
    </div>
  );
}

export default function TemplateEditor() {
  return <OrgAdminShell>{(orgId) => <Editor orgId={orgId} />}</OrgAdminShell>;
}
